import React, { useRef, useState, useCallback, useMemo } from 'react';
import { Layout } from 'fronton-react';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import {
  CalculatorPanel,
  CreditBlock,
  OtherSection,
  Section,
  UtpCardSection,
  InspGalleryRemote,
} from 'common/components';
import { COOKIES_STORE_NAME, selectIsInspGalleryABEnabled } from 'modules/cookies';
import { MODEL_JSON_STORE_NAME, useRegionIdSelector, selectInspirationGalleryData } from 'modules/model-json';
import { CategoriesSection } from 'common/components/kitchen';
import { KAZAKHSTAN } from 'common/constants/region-codes';
import { useIsomorphicLayoutEffect } from 'common/hooks';
import { KitchenGallery } from 'feature/kitchen-gallery';
import { PromoBanner } from 'remotes/promo-banner';
import { PromoBlock } from 'remotes/promo-block';
import { SpecialOffer } from 'remotes/special-offer';
import { KitchenBanner } from '../kitchen-banner';
import {
  KITCHEN_CALCULATOR_HREF,
  KITCHEN_ELEMENT_BLOCK,
  KITCHEN_LANDING_NAME,
  KITCHEN_SIGN_UP_HREF,
} from '../kitchen-constants';
import {
  KitchenDesignPanel,
  KitchenHeader,
  KitchenReviews,
  KitchenSignSection,
  KitchenVideo,
  KITCHEN_UTP_CARD_LIST,
} from './components';

/**
 * Вью страницы "Кухни"
 */
export const Kitchen = () => {
  const regionId = useRegionIdSelector();
  const [isStickyPanelVisible, setIsStickyPanelVisible] = useState<boolean>(true);
  const signSectionPanelRef = useRef<HTMLDivElement | null>(null);
  const signUpHref = `${KITCHEN_SIGN_UP_HREF}${regionId}`;
  const isRuRegion = regionId !== KAZAKHSTAN;

  const { cookies, modelJson } = useStoreon<State>(COOKIES_STORE_NAME, MODEL_JSON_STORE_NAME);
  const isInspGalleryABEnabled = useMemo(() => selectIsInspGalleryABEnabled(cookies), [cookies]);
  const inspirationGalleryData = useMemo(() => selectInspirationGalleryData(modelJson), [modelJson]);

  const handleScroll = useCallback(() => {
    if (signSectionPanelRef) {
      const heightVh = signSectionPanelRef.current?.offsetTop || 0;
      const scrollHeightWithWindow = window.scrollY + window.innerHeight;

      setIsStickyPanelVisible(scrollHeightWithWindow < heightVh);
    }
  }, [signSectionPanelRef]);

  useIsomorphicLayoutEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <>
      <KitchenHeader
        isRuRegion={isRuRegion}
        signUpHref={signUpHref}
      />
      <KitchenDesignPanel
        isConsultationFormVisible={!isStickyPanelVisible}
        isRuRegion={isRuRegion}
        signUpHref={signUpHref}
        withWhiteBackground
      />
      <Layout>
        <PromoBlock elementBlock={KITCHEN_ELEMENT_BLOCK} />
        <PromoBanner elementBlock={KITCHEN_ELEMENT_BLOCK} />
        <SpecialOffer elementBlock={KITCHEN_ELEMENT_BLOCK} />
        {isInspGalleryABEnabled ? (
          <InspGalleryRemote
            regionId={regionId}
            inspirationGalleryData={inspirationGalleryData}
            analyticsName="landingProektKuhni / ggr"
          />
        ) : (
          <KitchenGallery elementBlock={KITCHEN_ELEMENT_BLOCK} isRuRegion={isRuRegion} />
        )}
        <KitchenBanner elementBlock={KITCHEN_ELEMENT_BLOCK} />
      </Layout>
      {isRuRegion && (
        <>
          <Section>
            <CalculatorPanel
              calculatoHref={KITCHEN_CALCULATOR_HREF}
              description="Узнайте примерную стоимость кухни в&nbsp;онлайн-калькуляторе"
              elementBlock={KITCHEN_ELEMENT_BLOCK}
              elementName="Рассчитать стоимость кухни"
              title="Рассчитать стоимость кухни"
            />
          </Section>
          <Layout>
            <CreditBlock />
          </Layout>
        </>
      )}
      <KitchenReviews isRuRegion={isRuRegion} signUpHref={signUpHref} />
      <Layout>
        <UtpCardSection
          elementBlock={KITCHEN_ELEMENT_BLOCK}
          elementText="Дополнительные преимущества"
          renderModalContent={({ modal: { content: ModalContent } = {} }) =>
            ModalContent && <ModalContent />
          }
          utpCardList={KITCHEN_UTP_CARD_LIST}
        />
        <KitchenVideo />
      </Layout>
      <KitchenSignSection
        elementBlock={KITCHEN_ELEMENT_BLOCK}
        calculatorHref={KITCHEN_CALCULATOR_HREF}
        isRuRegion={isRuRegion}
        panelRef={signSectionPanelRef}
        signUpHref={signUpHref}
      />
      <Layout>
        <CategoriesSection elementBlock={KITCHEN_ELEMENT_BLOCK} />
      </Layout>
      <OtherSection
        elementBlock={`${KITCHEN_ELEMENT_BLOCK} / other`}
        landingName={KITCHEN_LANDING_NAME}
        promotionName={`${KITCHEN_ELEMENT_BLOCK}_bottom`}
      />
    </>
  );
};
